import { useMemo, useState } from 'react';
import { Tag, X } from 'lucide-react';
import { inputStyle, personLabel, secondaryBtn } from './gescomUi.jsx';

/**
 * Noms de tags d'une Person. Deux formats coexistent en base : `{ name, type? }`
 * (Synolog, Float) et la chaîne nue (legacy).
 */
function personTagNames(p) {
  if (!Array.isArray(p?.tags)) return [];
  return p.tags
    .map((t) => (typeof t === 'string' ? t : t && typeof t === 'object' ? String(t.name ?? '') : ''))
    .map((n) => n.trim())
    .filter(Boolean);
}

/**
 * Sélecteur multi-personnes (puces + recherche). `excluded` marque les puces
 * d'une sélection qui seront ignorées (personne aussi présente dans « Sauf »).
 *
 * La recherche propose aussi les tags de personne : choisir un tag ajoute d'un
 * coup toutes les personnes actives qui le portent (puces individuelles, donc
 * retirables une à une ensuite).
 */
export default function PersonPicker({ persons, selected, onChange, emptyLabel, emptyIcon, tone, struck }) {
  const [query, setQuery] = useState('');

  // Index tag → personnes, regroupé sans tenir compte de la casse.
  const tagIndex = useMemo(() => {
    const map = new Map();
    for (const p of persons ?? []) {
      for (const name of personTagNames(p)) {
        const key = name.toLowerCase();
        if (!map.has(key)) map.set(key, { name, persons: [] });
        const entry = map.get(key);
        if (!entry.persons.includes(p)) entry.persons.push(p);
      }
    }
    return map;
  }, [persons]);

  const options = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const selIds = new Set(selected.map((s) => s.id));
    return (persons ?? [])
      .filter((p) => !selIds.has(p.id))
      .filter((p) => !needle || personLabel(p).toLowerCase().includes(needle) || (p.email ?? '').toLowerCase().includes(needle))
      .slice(0, 8);
  }, [persons, query, selected]);

  const tagOptions = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return [];
    const selIds = new Set(selected.map((s) => s.id));
    return [...tagIndex.values()]
      .filter((t) => t.name.toLowerCase().includes(needle))
      .map((t) => ({ ...t, toAdd: t.persons.filter((p) => !selIds.has(p.id)) }))
      .sort((a, b) => a.name.localeCompare(b.name, 'fr'))
      .slice(0, 5);
  }, [tagIndex, query, selected]);

  const addTag = (t) => {
    if (t.toAdd.length === 0) return;
    onChange([...selected, ...t.toAdd.map((p) => ({ id: p.id, label: personLabel(p) }))]);
    setQuery('');
  };

  const chipColor = tone === 'danger' ? 'var(--danger)' : undefined;
  return (
    <>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
        {selected.map((s) => {
          const off = struck?.has(s.id);
          return (
            <span
              key={s.id}
              title={off ? 'Aussi présent dans « Sauf les collaborateurs » : ne sera pas synchronisé' : undefined}
              style={{ ...secondaryBtn, padding: '4px 8px', cursor: 'default', color: chipColor ?? (off ? 'var(--text-muted)' : 'var(--text)'), borderColor: chipColor ?? 'var(--glass-border)', textDecoration: off ? 'line-through' : 'none' }}
            >
              {s.label}
              <X size={12} style={{ cursor: 'pointer' }} onClick={() => onChange(selected.filter((x) => x.id !== s.id))} />
            </span>
          );
        })}
        {selected.length === 0 ? (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12.5, color: 'var(--text-muted)' }}>
            {emptyIcon} {emptyLabel}
          </span>
        ) : null}
      </div>
      <input
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Rechercher une personne ou un tag…"
        style={{ ...inputStyle, maxWidth: 360 }}
      />
      {query.trim() && (options.length > 0 || tagOptions.length > 0) ? (
        <div style={{ marginTop: 6, maxWidth: 360, border: '1px solid var(--glass-border)', borderRadius: 'var(--radius-sm)', overflow: 'hidden' }}>
          {tagOptions.map((t) => {
            const done = t.toAdd.length === 0;
            return (
              <button
                key={`tag:${t.name}`} type="button" disabled={done}
                title={done ? 'Toutes les personnes de ce tag sont déjà sélectionnées' : `Ajoute ${t.toAdd.length} personne(s) portant le tag « ${t.name} »`}
                onClick={() => addTag(t)}
                style={{ display: 'flex', alignItems: 'center', gap: 6, width: '100%', textAlign: 'left', padding: '6px 10px', background: 'transparent', border: 'none', borderBottom: '1px solid var(--glass-border)', color: done ? 'var(--text-muted)' : 'var(--text)', fontSize: 13, cursor: done ? 'default' : 'pointer' }}
              >
                <Tag size={13} style={{ color: 'var(--accent)', flexShrink: 0 }} />
                Tag « {t.name} »
                <span style={{ color: 'var(--text-muted)', marginLeft: 'auto' }}>
                  {done ? 'déjà sélectionné' : `+${t.toAdd.length} personne${t.toAdd.length > 1 ? 's' : ''}`}
                </span>
              </button>
            );
          })}
          {options.map((p) => (
            <button
              key={p.id} type="button"
              onClick={() => { onChange([...selected, { id: p.id, label: personLabel(p) }]); setQuery(''); }}
              style={{ display: 'block', width: '100%', textAlign: 'left', padding: '6px 10px', background: 'transparent', border: 'none', color: 'var(--text)', fontSize: 13, cursor: 'pointer' }}
            >
              {personLabel(p)} <span style={{ color: 'var(--text-muted)' }}>{p.email ?? ''}</span>
            </button>
          ))}
        </div>
      ) : null}
    </>
  );
}
